import { useState } from 'react'
import { toast } from 'sonner'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useModelSettings,
  useUpdateModelSettings,
  useCommunityHiddenEnterprises,
  useUpdateCommunityHiddenEnterprises,
  useOutageNotice,
  useUpdateOutageNotice,
  type ModelSettings,
  type CommunityHiddenEntry,
  type OutageNotice,
  type SsoSource,
} from '../api/admin-api'
import { useModels, type AIModel } from '@/hooks/useModels'

const NONE_VALUE = '__none__'

const entryKey = (entry: CommunityHiddenEntry) => `${entry.source}:${entry.enterpriseId}`

const entriesEqual = (a: CommunityHiddenEntry[], b: CommunityHiddenEntry[]) => {
  if (a.length !== b.length) return false
  return a.every(
    (entry, idx) => entry.source === b[idx].source && entry.enterpriseId === b[idx].enterpriseId
  )
}

const outageNoticeEqual = (a: OutageNotice, b: OutageNotice) =>
  a.enabled === b.enabled && a.message === b.message

function OutageNoticeSection() {
  const { data, isLoading, error } = useOutageNotice()
  const updateMutation = useUpdateOutageNotice()

  const [draft, setDraft] = useState<OutageNotice | null>(null)

  const server = data ?? null
  const current: OutageNotice = draft ?? server ?? { enabled: false, message: '' }
  const hasChanges = draft != null && server != null && !outageNoticeEqual(draft, server)

  const handleSave = async () => {
    const trimmedMessage = current.message.trim()
    if (!trimmedMessage) {
      toast.error('공지 메시지를 입력해주세요')
      return
    }
    try {
      await updateMutation.mutateAsync({
        enabled: current.enabled,
        message: trimmedMessage,
      })
      setDraft(null)
      toast.success('장애 공지가 저장되었습니다')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다')
    }
  }

  const handleReset = () => {
    setDraft(null)
  }

  if (isLoading) return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  if (error)
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>

  return (
    <section className="bg-white rounded-lg border p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-heading mb-2 tracking-tight">
          장애 공지
        </h2>
        <p className="text-sm text-caption leading-relaxed">
          활성화하면 모든 사용자에게 경고 배너가 노출되고 이미지 생성 요청이 차단됩니다.
          메시지는 줄바꿈을 포함할 수 있습니다.
        </p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor="outage-enabled" className="text-sm font-medium text-gray-700">
            공지 활성화
          </Label>
          <p className="text-xs text-caption">
            켜면 즉시 전체 사용자에게 적용됩니다.
          </p>
        </div>
        <Switch
          id="outage-enabled"
          checked={current.enabled}
          onCheckedChange={(checked) =>
            setDraft({
              enabled: checked,
              message: current.message,
            })
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="outage-message">공지 메시지</Label>
        <Textarea
          id="outage-message"
          rows={5}
          value={current.message}
          placeholder={'예) 2026-05-27 18:00~20:00 점검\n외부 API 장애로 이미지 생성을 일시 중단합니다.'}
          onChange={(e) =>
            setDraft({
              enabled: current.enabled,
              message: e.target.value,
            })
          }
        />
      </div>

      <div className="flex items-center gap-2 pt-2 border-t">
        <Button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
        >
          {updateMutation.isPending ? '저장 중...' : '저장'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          disabled={!hasChanges || updateMutation.isPending}
        >
          되돌리기
        </Button>
      </div>
    </section>
  )
}

function SettingsForm({
  initialSettings,
  models,
}: {
  initialSettings: ModelSettings
  models: AIModel[]
}) {
  const updateMutation = useUpdateModelSettings()

  const [defaultModelId, setDefaultModelId] = useState<string>(
    initialSettings.defaultModelId ?? models.find((m) => m.isDefault)?.id ?? ''
  )
  const [fallbacks, setFallbacks] = useState<Record<string, string | null>>(
    initialSettings.modelFallbacks ?? {}
  )

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        defaultModelId,
        modelFallbacks: fallbacks,
      })
      toast.success('모델 설정이 저장되었습니다')
    } catch (error) {
      toast.error(`저장 실패: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  const handleFallbackChange = (modelId: string, fallbackId: string) => {
    setFallbacks((prev) => ({
      ...prev,
      [modelId]: fallbackId === NONE_VALUE ? null : fallbackId,
    }))
  }

  return (
    <div className="space-y-8 bg-white p-6 rounded-lg border">
      {/* 기본 모델 설정 */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-heading tracking-tight">기본 모델</h2>
        <p className="text-sm text-caption">
          사용자가 모델을 선택하지 않았을 때 사용되는 기본 모델입니다.
        </p>
        <div className="space-y-2">
          <Label htmlFor="defaultModel">기본 모델</Label>
          <Select value={defaultModelId} onValueChange={setDefaultModelId}>
            <SelectTrigger id="defaultModel" className="w-full">
              <SelectValue placeholder="모델 선택" />
            </SelectTrigger>
            <SelectContent>
              {models.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  {model.name} ({model.id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* 모델별 Fallback 설정 */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-heading tracking-tight">모델별 Fallback</h2>
        <p className="text-sm text-caption">
          Rate limit 발생 시 자동으로 전환할 대체 모델을 설정합니다. 3회 재시도 후에도 rate
          limit이 해소되지 않으면 fallback 모델로 다시 시도합니다.
        </p>
        <div className="space-y-4">
          {models.map((model) => (
            <div key={model.id} className="space-y-2">
              <Label>{model.name}</Label>
              <Select
                value={fallbacks[model.id] ?? NONE_VALUE}
                onValueChange={(value) => handleFallbackChange(model.id, value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Fallback 없음" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>없음</SelectItem>
                  {models
                    .filter((m) => m.id !== model.id)
                    .map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name} ({m.id})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      {/* 저장 버튼 */}
      <div className="flex items-center space-x-4 pt-4">
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? '저장 중...' : '저장'}
        </Button>
      </div>
    </div>
  )
}

function CommunityHiddenEnterprisesSection() {
  const { data, isLoading, error } = useCommunityHiddenEnterprises()
  const updateMutation = useUpdateCommunityHiddenEnterprises()

  // null = 로컬 편집 없음 (서버 상태 미러)
  const [draftOverride, setDraftOverride] = useState<CommunityHiddenEntry[] | null>(null)
  const [sourceInput, setSourceInput] = useState<SsoSource>('wrks')
  const [inputValue, setInputValue] = useState('')

  const serverEntries = data?.entries ?? []
  const draft = draftOverride ?? serverEntries
  const setDraft = (next: CommunityHiddenEntry[]) => setDraftOverride(next)

  const hasChanges = draftOverride != null && !entriesEqual(draftOverride, serverEntries)

  const handleAdd = () => {
    const trimmed = inputValue.trim()
    if (!trimmed) return
    const parsed = Number.parseInt(trimmed, 10)
    if (!Number.isFinite(parsed) || parsed <= 0 || String(parsed) !== trimmed) {
      toast.error('양의 정수 enterprise ID만 입력 가능합니다')
      return
    }
    const candidate: CommunityHiddenEntry = { source: sourceInput, enterpriseId: parsed }
    if (draft.some((entry) => entryKey(entry) === entryKey(candidate))) {
      toast.error('이미 추가된 조합입니다')
      return
    }
    setDraft([...draft, candidate])
    setInputValue('')
  }

  const handleRemove = (target: CommunityHiddenEntry) => {
    setDraft(draft.filter((entry) => entryKey(entry) !== entryKey(target)))
  }

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(draft)
      setDraftOverride(null)
      toast.success('설정이 저장되었습니다')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다')
    }
  }

  const handleReset = () => {
    setDraftOverride(null)
  }

  if (isLoading) return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  if (error)
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>

  return (
    <section className="bg-white rounded-lg border p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-heading mb-2 tracking-tight">
          커뮤니티 메뉴 숨김
        </h2>
        <p className="text-sm text-caption leading-relaxed">
          여기에 등록된 source + enterprise ID 조합에 속한 사용자는 커뮤니티 메뉴가 노출되지 않으며,
          커뮤니티 이미지 조회/공유 API가 차단됩니다. enterprise ID 는 source 마다 1 부터 시작하므로
          반드시 source 와 함께 등록해야 합니다.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="enterprise-id-input">조합 추가</Label>
        <div className="flex gap-2">
          <Select value={sourceInput} onValueChange={(v) => setSourceInput(v as SsoSource)}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="wrks">wrks</SelectItem>
              <SelectItem value="gov">gov</SelectItem>
            </SelectContent>
          </Select>
          <Input
            id="enterprise-id-input"
            type="text"
            inputMode="numeric"
            placeholder="예: 12345"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAdd()
              }
            }}
          />
          <Button type="button" onClick={handleAdd}>
            <Plus className="w-4 h-4 mr-1" />
            추가
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>등록된 조합</Label>
        {draft.length === 0 ? (
          <div className="text-sm text-caption py-3">아직 등록된 항목이 없습니다.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {draft.map((entry) => (
              <div
                key={entryKey(entry)}
                className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-gray-100 rounded-full text-sm text-body"
              >
                <span className="font-mono">
                  <span className="text-caption">{entry.source}</span> {entry.enterpriseId}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemove(entry)}
                  className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-200 text-caption hover:text-body transition-colors"
                  aria-label={`${entry.source} ${entry.enterpriseId} 삭제`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t">
        <Button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
        >
          {updateMutation.isPending ? '저장 중...' : '저장'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          disabled={!hasChanges || updateMutation.isPending}
        >
          되돌리기
        </Button>
      </div>
    </section>
  )
}

export function SettingsPage() {
  const { data: settings, isLoading: settingsLoading, error: settingsError } = useModelSettings()
  const { data: models, isLoading: modelsLoading, error: modelsError } = useModels()

  const isLoading = settingsLoading || modelsLoading
  const error = settingsError || modelsError

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">Error: {error.message}</div>
  }

  if (!models || !settings) {
    return <div className="text-center py-12 text-red-500">Failed to load settings</div>
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-heading tracking-tight">설정</h1>
        <p className="text-caption mt-2 leading-relaxed">
          앱 전역 설정을 관리합니다
        </p>
      </div>

      <OutageNoticeSection />
      <CommunityHiddenEnterprisesSection />
      <SettingsForm initialSettings={settings} models={models} />
    </div>
  )
}
